<template>
  <div style="position: relative">
    <div class="d-flex align-center mb-3">
      <span class="subtitle-1">Q&A</span>
    </div>

    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div v-if="qaList.length > 0" class="qa-list">
      <div v-for="(qa, index) in qaList" :key="index" class="qa-item mb-7">
        <div class="d-flex flex-column" style="gap: 8px">
          <div class="d-flex justify-space-between align-start">
            <span class="qa-label">Question:</span>
            <div class="d-flex align-center" style="gap: 4px">
              <v-btn icon x-small @click="moveUp(index)" :disabled="index === 0 || loading">
                <v-icon small color="#9E9E9E">mdi-chevron-up</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                @click="moveDown(index)"
                :disabled="index === qaList.length - 1 || loading"
              >
                <v-icon small color="#9E9E9E">mdi-chevron-down</v-icon>
              </v-btn>
              <v-btn icon small class="edit-icon" @click="editQA(index)">
                <v-icon small color="#9E9E9E">mdi-pencil-outline</v-icon>
              </v-btn>
            </div>
          </div>
          <div class="qa-input-box">
            <span class="qa-text">{{ getEnglishText(qa.question) || 'Question detail...' }}</span>
          </div>
        </div>

        <div class="d-flex flex-column mt-2" style="gap: 10px">
          <span class="qa-label">Answer:</span>
          <div class="qa-input-box">
            <span class="qa-text">{{ getEnglishText(qa.answer) || 'Answer detail...' }}</span>
          </div>
        </div>
      </div>
    </div>

    <v-btn class="add-qa-btn" icon @click="openCreateDialog">
      <v-icon color="#9E9E9E" size="44">mdi-plus-circle-outline</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" max-width="800" persistent>
      <v-card>
        <v-card-title class="primaryDialogTitle">
          <span class="headline">{{ isEditing ? 'Edit Q&A' : 'Create New Q&A' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <fletrix-textarea
                v-model="formData.question.en"
                label="Question (EN)"
                rows="3"
                placeholder="Enter question in English"
                :error="!formData.question.en && showValidation"
                :error-messages="
                  !formData.question.en && showValidation ? 'English question is required' : ''
                "
              />
            </v-col>

            <v-col cols="12">
              <fletrix-textarea
                v-model="formData.question['zh-hk']"
                label="Question (ZH-HK)"
                rows="3"
                placeholder="輸入繁體中文問題"
              />
            </v-col>

            <v-col cols="12">
              <fletrix-textarea
                v-model="formData.question['zh-cn']"
                label="Question (ZH-CN)"
                rows="3"
                placeholder="输入简体中文问题"
              />
            </v-col>

            <v-col cols="12">
              <v-divider class="my-2"></v-divider>
            </v-col>

            <v-col cols="12">
              <fletrix-textarea
                v-model="formData.answer.en"
                label="Answer (EN)"
                rows="4"
                placeholder="Enter answer in English"
                :error="!formData.answer.en && showValidation"
                :error-messages="
                  !formData.answer.en && showValidation ? 'English answer is required' : ''
                "
              />
            </v-col>

            <v-col cols="12">
              <fletrix-textarea
                v-model="formData.answer['zh-hk']"
                label="Answer (ZH-HK)"
                rows="4"
                placeholder="輸入繁體中文答案"
              />
            </v-col>

            <v-col cols="12">
              <fletrix-textarea
                v-model="formData.answer['zh-cn']"
                label="Answer (ZH-CN)"
                rows="4"
                placeholder="输入简体中文答案"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-btn v-if="isEditing" color="error" text @click="confirmDelete">Delete</v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveQA">{{ isEditing ? 'Save' : 'Create' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api';
import { listQA, createQA, updateQA, deleteQA } from '@/api/qa';
import { RESTFUL } from '@/data/constants';
import { FletrixTextarea } from '@/components/Atom';

export default {
  name: 'QAManager',
  components: {
    FletrixTextarea,
  },
  data() {
    return {
      qaList: [],
      dialog: false,
      isEditing: false,
      editingIndex: null,
      showValidation: false,
      loading: false,
      formData: {
        question: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        answer: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
      },
    };
  },
  async mounted() {
    await this.loadQAList();
  },
  methods: {
    async loadQAList() {
      try {
        this.loading = true;
        const data = await listQA(api, {
          limit: 1000,
          sort: 'order',
        });
        this.qaList = data?.docs || data || [];
      } catch (error) {
        this.$emit('error', 'Failed to load Q&A list');
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.editingIndex = null;
      this.dialog = true;
      this.showValidation = false;
      this.resetFormData();
    },

    editQA(index) {
      this.isEditing = true;
      this.editingIndex = index;
      this.dialog = true;
      this.showValidation = false;

      const qa = this.qaList[index];
      this.formData = {
        question: this.parseMultiLang(qa.question),
        answer: this.parseMultiLang(qa.answer),
      };
    },

    parseMultiLang(data) {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        return {
          en: parsed.en || '',
          'zh-hk': parsed['zh-hk'] || '',
          'zh-cn': parsed['zh-cn'] || '',
        };
      } catch (e) {
        return {
          en: data || '',
          'zh-hk': '',
          'zh-cn': '',
        };
      }
    },

    getEnglishText(data) {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        return parsed.en || '';
      } catch (e) {
        return data || '';
      }
    },

    resetFormData() {
      this.formData = {
        question: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        answer: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
      };
    },

    closeDialog() {
      this.dialog = false;
      this.isEditing = false;
      this.editingIndex = null;
      this.showValidation = false;
      this.resetFormData();
    },

    async saveQA() {
      if (!this.formData.question.en?.trim() || !this.formData.answer.en?.trim()) {
        this.showValidation = true;
        return;
      }

      try {
        const payload = {
          question: JSON.stringify(this.formData.question),
          answer: JSON.stringify(this.formData.answer),
          order: this.isEditing ? this.qaList[this.editingIndex].order : this.qaList.length,
        };

        if (this.isEditing) {
          const qa = this.qaList[this.editingIndex];
          await updateQA(api, qa._id, payload);
          this.$emit('success', 'Q&A updated successfully');
        } else {
          await createQA(api, payload);
          this.$emit('success', 'Q&A created successfully');
        }

        this.closeDialog();
        await this.loadQAList();
      } catch (error) {
        this.$emit('error', `Failed to ${this.isEditing ? 'update' : 'create'} Q&A`);
      }
    },

    confirmDelete() {
      this.$root.$confirm
        .open('Confirm Delete', 'Are you sure you want to delete this Q&A?', {
          color: 'error',
        })
        .then(async confirm => {
          if (confirm) {
            await this.deleteQA();
          }
        });
    },

    async deleteQA() {
      try {
        const qa = this.qaList[this.editingIndex];
        await deleteQA(api, qa._id);
        this.$emit('success', 'Q&A deleted successfully');
        this.closeDialog();
        await this.loadQAList();
      } catch (error) {
        this.$emit('error', 'Failed to delete Q&A');
      }
    },

    async moveUp(index) {
      if (index === 0 || this.loading) return;
      await this.swapOrder(index, index - 1);
    },

    async moveDown(index) {
      if (index === this.qaList.length - 1 || this.loading) return;
      await this.swapOrder(index, index + 1);
    },

    async swapOrder(index1, index2) {
      try {
        this.loading = true;
        const qa1 = this.qaList[index1];
        const qa2 = this.qaList[index2];

        const order1 = qa1.order;
        const order2 = qa2.order;

        await updateQA(api, qa1._id, { order: order2 });
        await updateQA(api, qa2._id, { order: order1 });

        await this.loadQAList();
        this.$emit('success', 'Q&A order updated successfully');
      } catch (error) {
        this.$emit('error', 'Failed to update Q&A order');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.qa-list {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.qa-item {
  width: 100%;
}

.qa-label {
  font-family: 'Roboto Flex';
  font-weight: 700;
  font-size: 14px;
  line-height: 1.171875em;
  color: #5c5c5c;
}

.qa-input-box {
  width: 100%;
  min-height: 39px;
  background-color: #f5f5f5;
  border-radius: 20px;
  padding: 12px 32px;
  display: flex;
  align-items: center;
}

.qa-text {
  font-family: 'Roboto Flex';
  font-weight: 500;
  font-size: 14px;
  line-height: 1.171875em;
  color: #5c5c5c;
}

.edit-icon {
  margin-top: -4px;
}

.add-qa-btn {
  margin-top: 10px;
  width: 44px;
  height: 44px;
}
</style>
